import { QueryClient, QueryKey, useQuery, useQueryClient, UseQueryOptions } from "@tanstack/react-query";
import dayjs from "dayjs";
import firebase from "firebase/compat/app";
import { useEffect, useRef, useState } from "react";
import { useChunkedWork } from "../../hooks/useChunkedWork";
import { Campaign, City, Client, Country, FireFilter, GrailDoc, List, Tag, Talent, User } from '../../shared/types';
import { sleep } from "../../utils/core";
import { convertJsonToFirestoreTimestamp } from "../../utils/dates";
import { firestore, logDatabaseReads } from "./app";

// A persisted collection is a slowly changing list loaded with `useFresh*` (talent, bookers, clients, managers, sales).
// Persist is visible on first paint so a return visit is not an empty content area. `enabled` waits for paint so the Firestore refetch cannot freeze that paint.
const EMPTY_ARRAY: never[] = [];

function useAfterFirstPaint() {
    const [painted, setPainted] = useState(false);
    useEffect(() => {
        const id = requestAnimationFrame(() => setPainted(true));
        return () => cancelAnimationFrame(id);
    }, []);
    return painted;
}

// Wraps useQuery so a persisted collection does not attach until after first paint.
function useQueryAfterFirstPaint<T>(options: UseQueryOptions<T[]>) {
    const painted = useAfterFirstPaint();
    const callerEnabled = options.enabled ?? true;
    const query = useQuery({
        ...options,
        // The caller's `enabled` still gates Firestore; paint is an extra AND, not a replacement.
        // Do not `select` persist down to `[]` — React Query then treats the query as success and will not load.
        enabled: callerEnabled && painted,
    });

    const { dataUpdatedAt, refetch } = query;
    const didRefetchOnEnable = useRef(false);
    useEffect(() => {
        // Reset when either flag drops so a later enable (page-owned sequencing) can refetch again.
        if (!painted || !callerEnabled) {
            didRefetchOnEnable.current = false;
            return;
        }
        // The effect also re-runs when dataUpdatedAt or refetch changes; skip if this enable wave already started a refetch.
        if (didRefetchOnEnable.current) {
            return;
        }
        didRefetchOnEnable.current = true;
        // refetchOnMount: 'always' only runs at observer mount. We mount disabled until paint, so that window is gone.
        // Re-enable then fetches only if the query is stale; staleTime: Infinity means a persist hit never is.
        // Skip a cold query (dataUpdatedAt === 0); React Query already fetches that path when enabled becomes true.
        if (dataUpdatedAt > 0) {
            void refetch();
        }
    }, [painted, callerEnabled, dataUpdatedAt, refetch]);

    // Persist is visible so chunking can start on first paint. `isLoading` stays true until paint, and on a cold query with no data. A persist hit is not `isLoading` — `useFreshTalent` ORs `isSlicing` for that.
    const persistData = query.data
    return {
        ...query,
        data: persistData ?? EMPTY_ARRAY,
        isLoading: !painted || query.isLoading || (persistData == null && !query.isError),
        isFetching: !painted || query.isFetching,
    };
}

export function useFreshBookers({ enabled = true } = {}) {
    const queryKey = ['bookers'];
    const collectionName = 'users';
    const queryClient = useQueryClient();

    const query = useQueryAfterFirstPaint({
        queryKey: queryKey,
        queryFn: async () => getFreshCollection<User>(queryKey, queryClient, collectionName, {
            key: 'roles.booker',
            operator: "==",
            value: true
        }),
        staleTime: Infinity,
        refetchOnMount: 'always',
        onError: (err) => handleFreshQueryError(err, queryKey),
        enabled
    });

    return { flatData: query.data || [], query };
}

export function useFreshCampaigns({ limitToLast3Months = true, enabled = true } = {}) {
    const queryKey = ['campaigns', limitToLast3Months];
    const collectionName = 'campaigns';
    const queryClient = useQueryClient();

    const query = useQueryAfterFirstPaint({
        queryKey: queryKey,
        queryFn: async () => {
            if (limitToLast3Months) {
                return getFreshCollection<Campaign>(queryKey, queryClient, collectionName, {
                    key: "docMeta.updateTime", // NOTE... don't use createTime, because you can't have two inequality operators
                    operator: '>',
                    // NOTE - Setting to 5 months because we may have bookings that were dragging on
                    // e.g. if campaign/booking were created 4 months ago, but booking is updated since then and campaign isn't
                    value: dayjs().subtract(5, "months").set('date', 1).set('hour', 0).set('minute', 0).set('second', 0).toDate()
                })
            }
            return getFreshCollection<Campaign>(queryKey, queryClient, collectionName);
        },
        staleTime: Infinity,
        refetchOnMount: 'always',
        onError: (err) => handleFreshQueryError(err, queryKey),
        enabled
    });

    return { flatData: query.data || [], query };
}

// ID join / one RQ doc. Not a persisted collection.
interface useRelevantDocsProps {
    collectionName: string,
    docIds: string[],
    enabled: boolean,
}

export function useRelevantDocs<T>({ collectionName = '', docIds = [], enabled = true }: useRelevantDocsProps) {
    const queryKey = [collectionName, ...docIds];

    const query = useQuery({
        queryKey: queryKey,
        queryFn: async () => {
            const docPromises: Promise<firebase.firestore.DocumentSnapshot<firebase.firestore.DocumentData> | null>[] = [];

            for (const [idx, id] of docIds.entries()) {
                const ref = firestore.collection(collectionName).doc(id);

                // Failed gets must not poison the whole batch (a bare {} makes doc.data() throw).
                docPromises.push(ref.get().catch(() => null));

                if (idx % 200 === 0) {
                    await sleep(250);
                }
            }

            const docSnapshots = await Promise.all(docPromises);

            const docs = docSnapshots
                .filter((doc): doc is firebase.firestore.DocumentSnapshot<firebase.firestore.DocumentData> =>
                    Boolean(doc?.exists))
                .map((doc) => {
                    return {
                        id: doc.id,
                        ...doc.data()
                    } as T
                });

            logDatabaseReads(`useRelevantDocs - ${collectionName}`, docs.length);
            return docs;
        },
        // staleTime: Infinity,
        refetchOnMount: 'always',
        onError: (err) => console.warn(`Error in useFresh Query ::: ${(queryKey[0] as string).toLocaleUpperCase()}`, err),
        enabled,
        retry: true,
        retryDelay: (attemptIndex) => Math.min(1000 * 3 ** attemptIndex, 30000),
    });

    // console.log(`useRelevantDocs`, queryKey, query.isFetching);

    return query;
}

interface useSingleDocProps {
    collectionName: string,
    docId: string,
    enabled: boolean,
}

export function useSingleDoc<T>({ collectionName = '', docId, enabled = true }: useSingleDocProps) {
    const queryKey = [collectionName, docId];

    const query = useQuery({
        queryKey: queryKey,
        queryFn: async () => {


            const docRef = firestore.collection(collectionName).doc(docId);
            const docData = (await docRef.get()).data();
            logDatabaseReads(`useSingleDoc - ${collectionName}`, 1);

            return {
                ...docData,
                id: docId,
            } as T;

        },
        // staleTime: Infinity,
        refetchOnMount: 'always',
        onError: (err) => console.warn(`Error in useSingleDoc Query ::: ${(queryKey[0] as string).toLocaleUpperCase()}`, err),
        enabled,
        retry: true,
        retryDelay: (attemptIndex) => Math.min(1000 * 3 ** attemptIndex, 30000),
    });

    // console.log(`useRelevantDocs`, queryKey, query.isFetching);

    return query;
}

export function useFreshClients({ enabled = true } = {}) {
    const queryKey = ['clients'];
    const collectionName = 'clients';
    const queryClient = useQueryClient();

    const query = useQueryAfterFirstPaint({
        queryKey: queryKey,
        queryFn: async () => getFreshCollection<Client>(queryKey, queryClient, collectionName),
        staleTime: Infinity,
        refetchOnMount: 'always',
        onError: (err) => handleFreshQueryError(err, queryKey),
        enabled
    });

    return { flatData: query.data || [], query };
}

export function useFreshCountries() {
    const queryKey = ['countries'];
    const collectionName = 'countries';
    const queryClient = useQueryClient();

    const query = useQueryAfterFirstPaint({
        queryKey: queryKey,
        queryFn: async () => getFreshCollection<Country>(queryKey, queryClient, collectionName),
        staleTime: Infinity,
        refetchOnMount: 'always',
        onError: (err) => handleFreshQueryError(err, queryKey)
    });

    return { flatData: query.data || [], query };
}

// TJN TO-DO: Lists -- figure out why it's not caching when listId is provided...
export function useFreshLists({ listId, uid, creatorRequest }: { listId?: string, uid?: string, creatorRequest?: boolean }) {
    const queryKey = ['lists', listId, uid, creatorRequest];
    const collectionName = 'lists';
    const queryClient = useQueryClient();

    let filter: FireFilter | FireFilter[];

    if (listId) {
        filter = {
            key: "id",
            operator: "==",
            value: listId
        };
    } else if (uid) {
        filter = {
            key: "uid",
            operator: "==",
            value: uid
        };
    } else if (creatorRequest) {
        filter = {
            key: "creatorRequest",
            operator: "==",
            value: creatorRequest
        }
    } else {
        filter = [{
            key: "active",
            operator: "==",
            value: true
        }, {
            key: "uid",
            operator: "==",
            value: "system"
        }];
    }

    const query = useQueryAfterFirstPaint({
        queryKey: queryKey,
        queryFn: async () => getFreshCollection<List>(queryKey, queryClient, collectionName, filter),
        staleTime: Infinity,
        refetchOnMount: 'always',
        onError: (err) => handleFreshQueryError(err, queryKey)
    });

    return { flatData: query.data || [], query };
}

export function useFreshManagers({ enabled = true } = {}) { // TJN TO-DO: MAYBE - includeExternal = false
    const queryKey = ['managers'];
    const collectionName = 'users';
    const queryClient = useQueryClient();

    const query = useQueryAfterFirstPaint({
        queryKey: queryKey,
        queryFn: async () => {
            return Promise.all([
                getFreshCollection<User>(queryKey, queryClient, collectionName, { key: "roles.manager", operator: "==", value: true }),
                getFreshCollection<User>(queryKey, queryClient, collectionName, { key: "roles.externalManager", operator: "==", value: true }),
            ]).then((managerRes) => {
                const allManagers = managerRes.flat(1);
                const uniqueManagers = [...new Map(allManagers.map((manager) =>
                    [manager['email'], manager])).values()];
                return uniqueManagers;
            });
        },
        staleTime: Infinity,
        refetchOnMount: 'always',
        onError: (err) => handleFreshQueryError(err, queryKey),
        enabled
    });

    return { flatData: query.data || [], query };
}

export function useFreshSalesTeam({ enabled = true } = {}) { // TJN TO-DO: MAYBE - includeExternal = false
    const queryKey = ['salesTeam'];
    const collectionName = 'users';
    const queryClient = useQueryClient();

    const query = useQueryAfterFirstPaint({
        queryKey: queryKey,
        queryFn: async () => {
            return Promise.all([
                getFreshCollection<User>(queryKey, queryClient, collectionName, { key: "roles.salesTeam", operator: "==", value: true }),
            ]).then((managerRes) => {
                const allManagers = managerRes.flat(1);
                const uniqueManagers = [...new Map(allManagers.map((manager) =>
                    [manager['email'], manager])).values()];
                return uniqueManagers;
            });
        },
        staleTime: Infinity,
        refetchOnMount: 'always',
        onError: (err) => handleFreshQueryError(err, queryKey),
        enabled
    });

    return { flatData: query.data || [], query };
}


export function useFreshTags() {
    const queryKey = ['tags'];
    const collectionName = 'tags';
    const queryClient = useQueryClient();

    const query = useQueryAfterFirstPaint({
        queryKey: queryKey,
        queryFn: async () => getFreshCollection<Tag>(queryKey, queryClient, collectionName),
        staleTime: Infinity,
        refetchOnMount: 'always',
        onError: (err) => handleFreshQueryError(err, queryKey)
    });

    return { flatData: query.data || [], query };
}

export function useFreshCities() {
    const queryKey = ['cities'];
    const collectionName = 'cities';
    const queryClient = useQueryClient();

    const query = useQueryAfterFirstPaint({
        queryKey: queryKey,
        queryFn: async () => getFreshCollection<City>(queryKey, queryClient, collectionName),
        staleTime: Infinity,
        refetchOnMount: 'always',
        onError: (err) => handleFreshQueryError(err, queryKey)
    });

    return { flatData: query.data || [], query };
}

interface useFreshTalentProps {
    enabled?: boolean,
    listId?: string,
    managerId?: string,
    creatorUserId?: string,
    includeHidden?: boolean,
    includeDeleted?: boolean,
    superAdmin?: boolean,
}

export function useFreshTalent({
    enabled = true,
    managerId = undefined,
    creatorUserId = undefined,
    includeHidden = false,
    includeDeleted = false,
    superAdmin = false,
}: useFreshTalentProps = {}) {
    const queryKey = ['talent', managerId, creatorUserId, includeHidden, includeDeleted];
    const collectionName = 'talent';
    const queryClient = useQueryClient();

    const filter: FireFilter[] = [];
    if (managerId) {
        filter.push({ key: 'managerIds', operator: 'array-contains', value: managerId })
    }
    if (creatorUserId) {
        filter.push({ key: 'uid', operator: '==', value: creatorUserId })
    }
    if (!includeHidden) {
        filter.push({ key: 'hideUser', operator: '==', value: false })
    }
    if (!includeDeleted) {
        filter.push({ key: 'deleted', operator: '==', value: false })
    }

    const query = useQueryAfterFirstPaint({
        queryKey: queryKey,
        queryFn: async () => getFreshCollection<Talent>(queryKey, queryClient, collectionName, filter),
        staleTime: Infinity,
        refetchOnMount: 'always',
        onError: (err) => handleFreshQueryError(err, queryKey),
        enabled
    });

    // A disabled observer still sees persist `query.data` for this key. This hook does not walk that array.
    // A second `useFreshTalent` on the same key then cannot spend another `SLICE_MS` in the same frame.
    const rawTalent = enabled ? query.data : EMPTY_ARRAY as Talent[]
    const { result: flatData, isSlicing } = useChunkedWork(rawTalent, {
        createAcc: () => [] as Talent[],
        empty: EMPTY_ARRAY as Talent[],
        processChunk(slice, acc) {
            for (const tal of slice) {
                const ttHandle = tal.platformData?.TikTok?.username
                if (ttHandle !== "grailtest1" || includeHidden || superAdmin === true || window.location.hostname === "localhost") {
                    acc.push(tal)
                }
            }
        },
        finalize(acc) {
            acc.sort((tal1, tal2) => (tal2.platformData?.TikTok?.followers || 0) - (tal1.platformData?.TikTok?.followers || 0))
            return acc
        },
        workKey: `${includeHidden}:${superAdmin}`,
    })

    return { ...query, flatData, isLoading: query.isLoading || isSlicing || (flatData.length === 0 && rawTalent.length > 0), isFetching: query.isFetching || isSlicing };
}

// Same key as useFreshTalent with no managerId / creatorUserId and default hide/deleted filters.
export const FULL_TALENT_QUERY_KEY = ['talent', undefined, undefined, false, false];

export function warmFullTalent(queryClient: QueryClient) {
    return queryClient.fetchQuery({
        queryKey: FULL_TALENT_QUERY_KEY,
        queryFn: () => getFreshCollection<Talent>(FULL_TALENT_QUERY_KEY, queryClient, 'talent', [
            { key: 'hideUser', operator: '==', value: false },
            { key: 'deleted', operator: '==', value: false },
        ]),
        staleTime: Infinity,
    });
}

///////////////////////////////////
// React Query 'Fresh' Generics v2:
///////////////////////////////////
function isCompoundFilter(filter: FireFilter | FireFilter[]): filter is FireFilter[] {
    return (filter as FireFilter[]).length !== undefined;
}

// `useFresh*` queryFn (merge). Not the paint gate.
async function getFreshCollection<T extends GrailDoc>(queryKey: QueryKey, queryClient: QueryClient, collectionName: string, filter?: FireFilter | FireFilter[]) {
    const oldData: GrailDoc[] = queryClient.getQueryData(queryKey) || [];
    const updatedSince = findLargestTimestamp(oldData);

    let firebaseQuery: firebase.firestore.CollectionReference<firebase.firestore.DocumentData> | firebase.firestore.Query<firebase.firestore.DocumentData>
        = firestore.collection(collectionName);
    if (updatedSince) {
        firebaseQuery = firebaseQuery.where("docMeta.updateTime", ">", updatedSince);
    }

    let filterKey: string | undefined = undefined;
    if (filter && isCompoundFilter(filter)) {
        for (const fil of filter) {
            firebaseQuery = firebaseQuery.where(fil.key, fil.operator, fil.value);
        }
        filterKey = filter[0]?.key || undefined;
    } else if (filter) {
        firebaseQuery = firebaseQuery.where(filter.key, filter.operator, filter.value);
        filterKey = filter.key;
    }

    const snapshot = await firebaseQuery.get();
    logDatabaseReads(`getFreshCollection - ${collectionName}, ${filterKey}`, snapshot.docs.length, { key: 'docMeta.updateTime', operator: '>', value: updatedSince });

    const newData = snapshot.docs.map((doc) => {
        return {
            id: doc.id,
            ...doc.data()
        } as T
    });

    return flattenData<T>(oldData, newData);
}

function findLargestTimestamp(data: GrailDoc[]) {
    // Exact, and handles conversion from JSON
    if (!data.length) {
        return undefined;
    }
    let maxMs = 0;
    // loop to avoid Math.max(...hugeArray) stack/cost
    for (const item of data) {
        if (!item?.docMeta?.updateTime) continue;
        const ms = convertJsonToFirestoreTimestamp(item.docMeta.updateTime).toDate().getTime();
        if (ms > maxMs) maxMs = ms;
    }
    return maxMs ? new Date(maxMs) : undefined;
}

function flattenData<T>(oldData: GrailDoc[], newData: GrailDoc[]) {
    // Cold full fetch: old empty — return new as-is (avoid O(n²) findIndex merge).
    if (!oldData?.length) {
        return (newData || []) as T[];
    }
    if (!newData?.length) {
        return oldData as T[];
    }

    // Warm/delta merge by id — O(old+new). Previous findIndex-per-item was O(n²)
    // and froze the main thread (~18s on ~18k cold talent docs).
    const byId = new Map<string, GrailDoc>();
    for (const item of oldData) {
        if (item.id !== undefined) {
            byId.set(item.id, item);
        }
    }
    for (const item of newData) {
        if (item.id !== undefined) {
            byId.set(item.id, item);
        }
    }
    return Array.from(byId.values()) as T[];
}

function handleFreshQueryError(err: unknown, queryKey: QueryKey) {
    console.warn(`Error in useFresh Query ::: ${(queryKey[0] as string).toLocaleUpperCase()}`, err);
}
